import { useState } from 'react';
import { clsx } from 'clsx';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://fixtyer.co.uk';

interface ShareSuccessProps {
  amount: string;
  council: string;
  claimId: string;
}

export function ShareSuccess({ amount, council, claimId }: ShareSuccessProps) {
  const [copied, setCopied] = useState(false);

  const shareText = `I just recovered ${amount} for pothole damage from ${council} using @Fixtyer! 🎉 If you've had pothole damage, check them out:`;
  const shareUrl = `${SITE_URL}/?ref=${claimId.substring(0, 8)}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleTwitterShare = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, '_blank', 'width=550,height=420');
  };

  const handleFacebookShare = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(facebookUrl, '_blank', 'width=550,height=420');
  };

  return (
    <div className="bg-gradient-to-br from-success-50 to-success-100 rounded-2xl p-6 border border-success-200">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-success-500 rounded-xl flex items-center justify-center flex-shrink-0">
          <span className="text-2xl">🎉</span>
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg font-semibold text-success-900 mb-1">
            Congratulations on your successful claim!
          </h3>
          <p className="text-sm text-success-700 mb-4">
            Help other drivers by sharing your success story
          </p>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleTwitterShare}
              className={clsx(
                'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                'bg-[#1DA1F2] text-white hover:bg-[#1a8cd8]'
              )}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              Share on X
            </button>

            <button
              onClick={handleFacebookShare}
              className={clsx(
                'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                'bg-[#1877F2] text-white hover:bg-[#166fe5]'
              )}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              Share on Facebook
            </button>

            <button
              onClick={handleCopyLink}
              className={clsx(
                'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                'bg-white text-surface-700 border border-surface-300 hover:bg-surface-50'
              )}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              {copied ? 'Copied!' : 'Copy link'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
